import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { authSignup } from "../../lib/api";
import { track } from "../../lib/analytics";
import { useAuth } from "../../context/AuthContext";
import { Shell, Card, H1, Btn, Label, Input } from "./ui";

const VALUE = [
  ["Your own branded AI turf designer", "Launch a designer that looks 100% yours — logo, colours, company name."],
  ["Embed it on your website", "Drop one snippet onto any page. No developer needed."],
  ["Capture qualified quote leads", "Every visitor who designs turf becomes a lead in your dashboard."],
  ["Choose your plan after setup", "Start a 14-day free trial. Pick the plan that fits once you're live."],
];

export default function Signup() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const source = sp.get("source") || "direct";
  const { refresh } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", company: "", website: "", phone: "", country: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const valid = f.name && /.+@.+\..+/.test(f.email) && f.password.length >= 8 && f.company;
  useEffect(() => { track("signup_started", { source }); }, [source]);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await authSignup({ ...f, source });
      track("signup_completed", { source });
      await refresh?.();
      toast.success("Account created — let's set up your designer");
      nav("/onboarding", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create your account");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="grid lg:grid-cols-2 gap-10 items-start mt-6" data-testid="signup-page">
        <div className="lg:pt-6">
          <div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-3">Launch in under 10 minutes</div>
          <H1>Your own branded AI turf designer — that generates leads</H1>
          <p className="text-white/60 mt-4 text-base leading-relaxed">Turf, gym-flooring and sports-facility companies use our designer to let customers create custom turf online, then capture them as qualified quote leads. Start free for 14 days.</p>
          <div className="mt-8 space-y-4">
            {VALUE.map(([t, d]) => (
              <div key={t} className="flex gap-3">
                <div className="text-[#00D26A] mt-0.5">✓</div>
                <div><div className="text-white font-semibold text-sm">{t}</div><div className="text-white/50 text-sm">{d}</div></div>
              </div>
            ))}
          </div>
        </div>

        <Card className="lg:sticky lg:top-6">
          <h2 className="text-xl font-semibold mb-1">Create your account</h2>
          <p className="text-white/40 text-sm mb-5">No card needed to sign up. Choose a plan after setup.</p>
          <div className="grid gap-3">
            <div><Label>Your name*</Label><Input value={f.name} onChange={set("name")} data-testid="signup-name" /></div>
            <div><Label>Work email*</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="signup-email" /></div>
            <div><Label>Password* (min 8 chars)</Label><Input type="password" value={f.password} onChange={set("password")} data-testid="signup-password" /></div>
            <div><Label>Company name*</Label><Input value={f.company} onChange={set("company")} data-testid="signup-company" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Website</Label><Input value={f.website} onChange={set("website")} data-testid="signup-website" placeholder="https://" /></div>
              <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="signup-phone" /></div>
            </div>
            <div><Label>Country</Label><Input value={f.country} onChange={set("country")} data-testid="signup-country" placeholder="United Kingdom" /></div>
          </div>
          <Btn onClick={submit} disabled={!valid || busy} data-testid="signup-submit" className="w-full mt-5">{busy ? "Creating…" : "Create account & continue"}</Btn>
          <p className="text-white/40 text-xs text-center mt-4">Already have an account? <Link to="/login" className="text-[#00D26A]">Sign in</Link></p>
        </Card>
      </div>
    </Shell>
  );
}
